import { css, cx } from "@emotion/css";
import { ActionButton, ActionButtonType, Callout, PopoverBasicWithTrigger, tableBorder } from "@octopusdeploy/design-system-components";
import { PlusIcon } from "@octopusdeploy/design-system-icons";
import { borderWidth, fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ChannelResource, EnvironmentV2Resource, FeedResource, GetTeamsLookupBffResponseTeam, GitRefResource, PackageReference, PlatformHubAccountSummary, ProcessTemplateParameterResource, ProcessTemplateResource, ProjectSummaryResource, PropertyValueResource, ScopeValues, VariableSetResource, WorkerPoolResource, } from "@octopusdeploy/octopus-server-client";
import { ControlType, VariableType, getParametersOfType } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { flatten } from "lodash";
import * as React from "react";
import { useMemo, useState, useRef } from "react";
import type { StepEditorEvent } from "~/analytics/Analytics";
import { useScopeWidth } from "~/areas/platformHub/processEditor/useScopeWidth";
import type { ParameterValueRow, ProcessTemplateValueKey } from "~/areas/platformHub/processTemplates/ProcessTemplateParametersTab";
import { baseCellHeightInRem, createProcessTemplateValueKey, isAccountControlType, isAccountControlTypeWithoutDefaultValues, multipleValuesAndScopesIndicatorHeightInRem, ProcessTemplateParameterScopeCell, showMultipleValuesAndScopesIndicators, } from "~/areas/platformHub/processTemplates/ProcessTemplateParametersTab";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import type { FailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import type { StoredAction } from "~/areas/projects/components/Process/types";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import ReadonlyInlineVariableValue from "~/areas/projects/components/Variables/TenantVariables/ReadonlyInlineVariableValue";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import { useProjectContext } from "~/areas/projects/context";
import MissingVariablesIcon from "~/areas/tenants/MissingVariablesIcon/MissingVariablesIcon";
import { isAccountType } from "~/areas/variables/isAccountType";
import { repository } from "~/clientInstance";
import type { ProjectSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import ActionTemplateParameterInput from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import { createSummaryFor } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInputExpandableFormElement";
import { getLabel } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInputRenderer";
import { AccountParametersContextualHelp } from "~/components/ContextualHelp/ContextualHelpSnippets";
import { type DoBusyTask } from "~/components/DataBaseComponent/index";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { IconButtonWithTooltip } from "~/components/IconButtonWithTooltip";
import Markdown from "~/components/Markdown";
import PopoverWhenFocused from "~/components/PopoverWhenFocused";
import convertPropertyValueResourceToString from "~/components/convertPropertyValueResourceToString";
import { FormSection, FormSectionHeading, Summary, Text } from "~/components/form/index";
import type { Options } from "~/primitiveComponents/form/Select/Options";
import selectOptionsToItems from "~/primitiveComponents/form/Select/Options";
import stringHelper from "~/utils/StringHelper/index";
import { stringifyActionProperty } from "~/utils/actionPropertyUtils";
import { JsonUtils } from "~/utils/jsonUtils";
interface ProcessTemplateActionParameterValuesProps {
    stepId: string;
    action: StoredAction;
    doBusyTask: DoBusyTask;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    processTemplate: ProcessTemplateResource;
    gitRefResource: GitRefResource | undefined;
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    environments: EnvironmentV2Resource[];
    availableTargetTags: string[];
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    availableWorkerPools: WorkerPoolResource[];
    teams: GetTeamsLookupBffResponseTeam[];
    isTenanted: boolean;
    channels: ChannelResource[];
    projects: ProjectSummaryResource[];
    refreshVariables: () => Promise<void>;
}
export function ProcessTemplateActionParameterValues({ stepId, action, doBusyTask, onParameterValueChanged, processTemplate, gitRefResource, processVariableSet, libraryVariableSets, environments, availableTargetTags, analyticsStepEditorDispatch, availableWorkerPools, isTenanted, teams, channels, projects, refreshVariables, }: ProcessTemplateActionParameterValuesProps) {
    const projectContext = useProjectContext();
    const processContext = useProcessContext();
    const actionNames: string[] = flatten(Object.values(processContext.state.model.actions.byId).map((a) => a.Name));
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const [parameterWorkerPools, setParameterWorkerPools] = useState(availableWorkerPools);
    const platformHubAccountIds = useMemo(() => processTemplate.Parameters.filter((p) => isAccountType(getVariableTypeFromDisplaySettings(p.DisplaySettings["Octopus.ControlType"]))).flatMap((p) => p.Values.map((v) => v.Value)), [processTemplate.Parameters]);
    const { result: platformHubAccountNames } = useQuery(() => repository.PlatformHubAccounts.getAccountNames(platformHubAccountIds), [platformHubAccountIds], "Get platform hub account names");
    const packageReferences: PackageReference[] = getParametersOfType(processTemplate.Parameters, ControlType.Package).map((packageParam) => {
        const existingPackage = action.Packages.find((existingPackage) => packageParam.Name === existingPackage.Name);
        return {
            Name: packageParam.Name,
            PackageId: "",
            FeedId: "",
            AcquisitionLocation: "",
            Properties: {
                PackageParameterName: packageParam.Name,
                SelectionMode: "deferred",
            },
            Id: "",
            Version: existingPackage?.Version,
        };
    });
    if (!platformHubAccountNames) {
        return null;
    }
    const sourceItems: ProjectSourceItems = {
        stepNames: actionNames,
        projectId: projectContext.state.model.Id,
        processIdentifier: processContext.state.processIdentifier,
        processVariableSet,
        libraryVariableSets,
        refreshVariables,
        gitRef: gitRefResource,
        packages: {
            items: packageReferences,
            feeds: feeds,
            onRequestRefresh: async () => {
                await doBusyTask(async () => {
                    await refreshFeeds();
                });
            },
            setPackages: (packages: PackageReference[]) => {
                processContext.actions.setActionMetaProperties(action.Id, (prev) => ({ ...prev, Packages: packages }));
            },
        },
        availableTargetTags,
        environments,
        workerPools: {
            items: parameterWorkerPools,
            onRequestRefresh: () => doBusyTask(async () => {
                const summaries = await repository.WorkerPools.summary();
                setParameterWorkerPools(summaries.WorkerPoolSummaries.map((s) => s.WorkerPool));
            }),
        },
        teams,
        channels,
        projects,
    };
    const parameterNamesInAction = Object.keys(action.Properties).filter((p) => !p.startsWith("Octopus"));
    const parameterNamesInProcessTemplate = new Set(processTemplate.Parameters.map((p) => p.Name));
    const parametersRemovedFromProcessTemplate = parameterNamesInAction.filter((p) => !parameterNamesInProcessTemplate.has(p));
    const requiredParameters = processTemplate.Parameters.filter((p) => requiresValue(p, action, availableWorkerPools, isTenanted));
    const parameterValueRows = getParameterValueRows(requiredParameters, action, sourceItems);
    const accountParameterValueRows = parameterValueRows.filter((row) => isAccountControlType(getControlTypeFromParameterValueRow(row)));
    const otherParameterValueRows = parameterValueRows.filter((row) => !isAccountControlType(getControlTypeFromParameterValueRow(row)));
    const clearValues = () => {
        processContext.actions.removeActionProperties(action.Id, parametersRemovedFromProcessTemplate);
    };
    return (<>
            {requiredParameters.length > 0 ? (<>
                    {parametersRemovedFromProcessTemplate.length > 0 ? (<Callout type={"information"} title={"Clear old parameter values"}>
                            <div className={styles.calloutContainer}>
                                <div>One or more parameters have been removed from the current version of the process template. We recommend clearing these values to avoid any unexpected behavior.</div>
                                <div>
                                    <ActionButton type={ActionButtonType.Primary} label={"Clear values"} onClick={clearValues}/>
                                </div>
                            </div>
                        </Callout>) : null}
                    {accountParameterValueRows.length > 0 && (<>
                            <FormSectionHeading title={<div className={styles.accountParametersHeading}>
                                        <div>Scoped parameters</div>
                                        <AccountParametersContextualHelp />
                                    </div>}/>
                            <ProcessTemplateParameterValuesTable parameterValueRows={accountParameterValueRows} availableScopeValues={{ ...defaultScopeValues, Environments: environments }} platformHubAccounts={platformHubAccountNames} stepId={stepId} sourceItems={sourceItems} action={action} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch}/>
                        </>)}
                    {otherParameterValueRows.length > 0 && (<>
                            <FormSectionHeading title="Unscoped parameters"/>
                            <ProcessTemplateParameterValuesTable parameterValueRows={otherParameterValueRows} availableScopeValues={{ ...defaultScopeValues, Environments: environments }} platformHubAccounts={platformHubAccountNames} stepId={stepId} sourceItems={sourceItems} action={action} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch} hideScopeColumn={true}/>
                        </>)}
                    {parametersRemovedFromProcessTemplate.length > 0 && (<>
                            <FormSectionHeading title="Old Parameter Values"/>
                            {parametersRemovedFromProcessTemplate.map((parameter) => (<FormSection key={parameter} title={<span className={styles.removedParameter}>{parameter}</span>} includeBorder={true} help={<span className={styles.removedParameter}>{stringifyActionProperty(action.Properties[parameter])}</span>}></FormSection>))}
                        </>)}
                </>) : null}
        </>);
}
export const requiresValue = (parameter: ProcessTemplateParameterResource, action: StoredAction, availableWorkerPools: WorkerPoolResource[], isTenanted: boolean) => {
    if (parameter.DisplaySettings["Octopus.ControlType"] === ControlType.WorkerPool && availableWorkerPools.length === 0) {
        return false;
    }
    if (parameter.DisplaySettings["Octopus.ControlType"] === ControlType.TenantTags) {
        const value = convertPropertyValueResourceToString(action.Properties[parameter.Name]);
        if (!isTenanted && stringHelper.isNullOrWhiteSpace(value)) {
            return false;
        }
        return true;
    }
    return true;
};
interface ProcessTemplateParameterValuesTableProps {
    parameterValueRows: ParameterValueRow[];
    availableScopeValues: ScopeValues;
    platformHubAccounts: PlatformHubAccountSummary[];
    stepId: string;
    sourceItems: ProjectSourceItems;
    action: StoredAction;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    hideScopeColumn?: boolean;
}
function ProcessTemplateParameterValuesTable({ parameterValueRows, availableScopeValues, platformHubAccounts, stepId, sourceItems, action, onParameterValueChanged, doBusyTask, analyticsStepEditorDispatch, hideScopeColumn, }: ProcessTemplateParameterValuesTableProps) {
    const { scopeHeaderRef, scopeWidth } = useScopeWidth();
    const [focusedValueKey, setFocusedValueKey] = useState<ProcessTemplateValueKey | null>(null);
    const nameColumnWidthAsPercentage = 26;
    const valueAndScopeColumnWidthAsPercentage = 37;
    return (<div>
            <GroupedDataTable data={parameterValueRows} columns={[
            {
                title: "Name",
                render: (data) => (data.ValueIndex === 0 ? <ReadonlyParameterNameCell parameter={data.Parameter}/> : null),
                width: `${nameColumnWidthAsPercentage}%`,
                isChild: (data) => data.ValueIndex !== 0,
                padding: space[12],
                collapseNullCellsUpwards: true,
                verticalAlign: "top",
            },
            {
                title: "Value",
                render: (data) => (<ProcessTemplateConsumerParameterValueCell data={data} stepId={stepId} sourceItems={sourceItems} action={action} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask} analyticsStepEditorDispatch={analyticsStepEditorDispatch} platformHubAccounts={platformHubAccounts} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey}/>),
                width: hideScopeColumn ? `${2 * valueAndScopeColumnWidthAsPercentage}%` : `${valueAndScopeColumnWidthAsPercentage}%`,
                padding: "0",
            },
            ...(hideScopeColumn
                ? []
                : [
                    {
                        title: "Scope",
                        render: (data: ParameterValueRow) => {
                            const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
                            const getScopeCellContents = () => {
                                if (!data.Value) {
                                    return null;
                                }
                                const uniqueAccountVariableNames = getUniqueAccountProcessVariableNames(sourceItems);
                                if (uniqueAccountVariableNames.includes(data.Value.Value)) {
                                    return <div className={cx(styles.disabledText, styles.valueContainer)}>Defined by project variable</div>;
                                }
                                return (<ProcessTemplateParameterScopeCell parameter={data.Parameter} value={data.Value} onChange={noOp} index={data.ValueIndex} isEditing={false} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} scopeValues={availableScopeValues} height={48} width={scopeWidth} isConsumer={true}/>);
                            };
                            return (<div>
                                              {showMultipleValuesAndScopesIndicators(controlType, data.ValueIndex, data.Value, data.Parameter) && (<div className={styles.multipleValuesAndScopesIndicator}>
                                                      <div>Multiple scopes</div>
                                                  </div>)}
                                              <div className={styles.scopeContainer}>{getScopeCellContents()}</div>
                                          </div>);
                        },
                        width: `${valueAndScopeColumnWidthAsPercentage}%`,
                        padding: "0",
                        ref: scopeHeaderRef,
                    },
                ]),
        ]} getRowKey={(data) => `${data.Parameter.Name}-${data.ValueIndex}`}/>
        </div>);
}
function ReadonlyParameterNameCell({ parameter }: {
    parameter: ProcessTemplateParameterResource;
}) {
    return (<div>
            <div className={styles.parameterNameCellPrimaryHeader}>{parameter.Label || parameter.Name}</div>
            {parameter.Label && <div className={styles.parameterNameCellSecondaryHeader}>{parameter.Name}</div>}
            <div className={styles.parameterHelpText}>
                <Markdown markup={parameter.HelpText} noMargins={true} mutedTextColor={true}/>
            </div>
        </div>);
}
const defaultScopeValues: ScopeValues = {
    Actions: [],
    Channels: [],
    Environments: [],
    Machines: [],
    Roles: [],
    TenantTags: [],
    Processes: [],
};
interface ProcessTemplateParameterProps {
    parameter: ProcessTemplateParameterResource;
    stepId: string;
    sourceItems: ProjectSourceItems;
    action: StoredAction;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    autoFocus: boolean;
}
function ProcessTemplateParameter({ stepId, parameter, sourceItems, action, onParameterValueChanged, doBusyTask, analyticsStepEditorDispatch, autoFocus }: ProcessTemplateParameterProps) {
    const type = parameter.DisplaySettings["Octopus.ControlType"];
    const value = action.Properties[parameter.Name];
    const processErrorSelectors = useProcessErrorSelectors();
    const getStepFieldError = (value: string) => processErrorSelectors.getStepFieldError(stepId, value);
    return type ? (<div className={styles.parameterContainer}>
            <ActionTemplateParameterInput parameter={parameter} sourceItems={sourceItems} doBusyTask={doBusyTask} value={value} onChange={(value) => onParameterValueChanged(parameter.Name, value)} actionType={action.ActionType} error={getStepFieldError(parameter.Name)} analyticsStepEditorDispatch={analyticsStepEditorDispatch} autoFocus={autoFocus}/>
        </div>) : null;
}
const styles = {
    calloutContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
    }),
    removedParameter: css({
        color: themeTokens.color.text.disabled,
    }),
    parameterNameCellPrimaryHeader: css({
        font: text.body.regular.small,
        fontWeight: fontWeight["700"],
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    parameterNameCellSecondaryHeader: css({
        font: text.code.regular.small,
        color: themeTokens.color.text.tertiary,
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    parameterHelpText: css({
        font: text.body.regular.small,
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    valueContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
        width: "100%",
        height: `${baseCellHeightInRem}rem`,
        display: "flex",
        alignItems: "center",
    }),
    disabledText: css({
        color: themeTokens.color.text.disabled,
    }),
    popoverWhenFocusedContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
        width: "100%",
    }),
    popoverBasicWithTriggerContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
    }),
    noValue: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        gap: space[8],
    }),
    optionalText: css({
        color: themeTokens.color.text.tertiary,
        marginLeft: space[4],
    }),
    helpText: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    scopeContainer: css({
        height: `${baseCellHeightInRem}rem`,
        width: "100%",
        cursor: "default",
    }),
    multipleValuesAndScopesIndicator: css({
        height: `${multipleValuesAndScopesIndicatorHeightInRem}rem`,
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.tertiary,
        borderBottom: tableBorder,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingLeft: space[16],
        paddingRight: space[16],
    }),
    overrideButtonContainer: css({
        display: "flex",
        alignItems: "center",
    }),
    parameterContainer: css({
        paddingTop: space[16],
        paddingLeft: space[4],
        paddingRight: space[4],
    }),
    summaryNode: css({
        width: "100%",
        borderBottom: `${borderWidth[2]} solid transparent`,
        "&:hover": {
            borderBottom: `${borderWidth[2]} solid ${themeTokens.color.textField.border.hover}`,
        },
    }),
    accountParametersHeading: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[8],
    }),
};
const isAccountVariableType = (variableType: VariableType): boolean => {
    return (variableType === VariableType.AmazonWebServicesAccount ||
        variableType === VariableType.AzureAccount ||
        variableType === VariableType.GoogleCloudAccount ||
        variableType === VariableType.UsernamePasswordAccount ||
        variableType === VariableType.GenericOidcAccount);
};
const getUniqueAccountProcessVariableNames = (sourceItems: ProjectSourceItems): string[] => {
    const processVariables = isVariableSetResource(sourceItems.processVariableSet) ? sourceItems.processVariableSet.Variables : [];
    return [...new Set(processVariables.filter((v) => isAccountVariableType(v.Type)).map((v) => v.Name))];
};
const actionHasAccountParameterOverride = (action: StoredAction, parameterName: string, controlType: ControlType): boolean => {
    return !!action.Properties[parameterName] && typeof action.Properties[parameterName] === "string" && isAccountControlType(controlType);
};
const isOverrideVariableRow = (row: ParameterValueRow, sourceItems: ProjectSourceItems): boolean => {
    if (!row.Value) {
        return false;
    }
    const uniqueAccountVariableNames = getUniqueAccountProcessVariableNames(sourceItems);
    return uniqueAccountVariableNames.includes(row.Value.Value);
};
const createSummaryForParameterValue = (label: string, type: ControlType, options: Options, feeds: FeedResource[], environments: EnvironmentV2Resource[], workerPools: WorkerPoolResource[], teams: GetTeamsLookupBffResponseTeam[], channels: ChannelResource[], projects: ProjectSummaryResource[], packageParameterIsUsed?: boolean) => {
    // Override the createSummaryFor() function to handle package parameters a specific way
    return (value: PropertyValueResource | undefined) => {
        if (typeof value === "string" && type === ControlType.Package) {
            if (JsonUtils.tryParseJson(value)) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
                const pkg = JSON.parse(value) as Partial<PackageReference<any>>;
                if (!pkg.PackageId) {
                    return Summary.placeholder(<div className={styles.noValue}>
                            <MissingVariablesIcon show={true}/>
                            <Text value={""} placeholder={"Provide value"} onChange={noOp} hideUnderline={true} monoSpacedFont={true}/>
                        </div>);
                }
                return createSummaryFor(label, type, options, feeds, environments, workerPools, teams, channels, projects, packageParameterIsUsed)(value);
            }
        }
        return createSummaryFor(label, type, options, feeds, environments, workerPools, teams, channels, projects, packageParameterIsUsed)(value);
    };
};
const renderSummaryNodeForParameterValue = (node: React.ReactNode) => {
    if (typeof node === "string") {
        return <Text value={node.toString()} placeholder={node.toString()} onChange={noOp} hideUnderline={true} showValueAsTitleAttribute={true} monoSpacedFont={true}/>;
    }
    return <div className={styles.summaryNode}>{node}</div>;
};
const isVariableSetResource = (resource: VariableSetResource | FailedPermissionCheck): resource is VariableSetResource => {
    if ("Variables" in resource) {
        return true;
    }
    return false;
};
const getControlTypeFromParameterValueRow = (row: ParameterValueRow): ControlType => {
    return row.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
};
function getParameterValueRows(parameters: ProcessTemplateParameterResource[], action: StoredAction, sourceItems: ProjectSourceItems): ParameterValueRow[] {
    return parameters.flatMap((p, parameterIndex): ParameterValueRow[] => {
        let parameterWithOverrides = { ...p };
        let overrideVariableRow: ParameterValueRow | undefined = undefined;
        let startingValueIndex = 0;
        const controlType = p.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
        if (p.Values.length === 0) {
            return [{ Parameter: p, Value: null, ParameterIndex: parameterIndex, ValueIndex: 0 }];
        }
        if (actionHasAccountParameterOverride(action, p.Name, controlType)) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const overrideVariableName = action.Properties[p.Name] as string;
            overrideVariableRow = {
                Parameter: p,
                Value: { Value: overrideVariableName, Scope: {}, OverridenScope: {} },
                ParameterIndex: parameterIndex,
                ValueIndex: startingValueIndex,
            };
            let completeOverrideVariableEnvironmentScope: string[] = [];
            if (isVariableSetResource(sourceItems.processVariableSet)) {
                for (const variable of sourceItems.processVariableSet.Variables) {
                    if (variable.Name === overrideVariableName) {
                        const overrideVariableEnvironmentScope = variable.Scope?.Environment?.filter((e) => e !== undefined) ?? [];
                        if (overrideVariableEnvironmentScope.length > 0) {
                            completeOverrideVariableEnvironmentScope = [...completeOverrideVariableEnvironmentScope, ...overrideVariableEnvironmentScope];
                        }
                    }
                }
            }
            parameterWithOverrides = {
                ...p,
                Values: p.Values.map((v) => ({
                    ...v,
                    OverridenScope: { Environment: completeOverrideVariableEnvironmentScope },
                    OverrideValue: overrideVariableName,
                })),
            };
            startingValueIndex = 1;
        }
        return [
            ...(overrideVariableRow ? [overrideVariableRow] : []),
            ...parameterWithOverrides.Values.map<ParameterValueRow>((v, valueIndex) => ({ Parameter: parameterWithOverrides, Value: v, ParameterIndex: parameterIndex, ValueIndex: startingValueIndex + valueIndex })),
        ];
    });
}
interface ProcessTemplateConsumerParameterValueCellProps {
    data: ParameterValueRow;
    stepId: string;
    sourceItems: ProjectSourceItems;
    action: StoredAction;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    platformHubAccounts: PlatformHubAccountSummary[];
    focusedValueKey: ProcessTemplateValueKey | null;
    setFocusedValueKey: (key: ProcessTemplateValueKey | null) => void;
}
function ProcessTemplateConsumerParameterValueCell(props: ProcessTemplateConsumerParameterValueCellProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const valueKey = createProcessTemplateValueKey(props.data.Parameter.Name, props.data.ValueIndex, "Value");
    const onFocus = () => props.setFocusedValueKey(valueKey);
    const onBlur = () => props.setFocusedValueKey(null);
    const controlType = props.data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    const isFocused = props.focusedValueKey === valueKey;
    const rowCanBeFocused = !isAccountControlType(controlType) || isAccountControlTypeWithoutDefaultValues(props.data) || isOverrideVariableRow(props.data, props.sourceItems);
    const rowIsFocused = isFocused && rowCanBeFocused;
    const hasValue = () => {
        return props.action.Properties[props.data.Parameter.Name] || props.data.Value;
    };
    const getValue = () => {
        if (props.action.Properties[props.data.Parameter.Name] && (!isAccountControlType(controlType) || isAccountControlTypeWithoutDefaultValues(props.data))) {
            return props.action.Properties[props.data.Parameter.Name];
        }
        else if (props.data.Value) {
            return props.data.Value?.Value;
        }
        return undefined;
    };
    const getAccounts = () => {
        const platformHubAccountIdToNameObject = props.platformHubAccounts.reduce((acc, a) => ({ ...acc, [a.Id]: a.Name }), {});
        const uniqueAccountVariableNames = getUniqueAccountProcessVariableNames(props.sourceItems);
        const accountVariableNameToFormattedNameObject = uniqueAccountVariableNames.reduce((acc, name) => ({ ...acc, [name]: name }), {});
        return { ...platformHubAccountIdToNameObject, ...accountVariableNameToFormattedNameObject };
    };
    const tooltipContent = isOverrideVariableRow(props.data, props.sourceItems) ? "You can only provide a single value" : "Override with a project variable";
    const renderValue = () => {
        if (isAccountControlType(controlType) || controlType === ControlType.Sensitive) {
            return (<ReadonlyInlineVariableValue value={getValue()} variableType={getVariableTypeFromDisplaySettings(props.data.Parameter.DisplaySettings["Octopus.ControlType"])} defaultValue={undefined} accounts={getAccounts()} certificates={{}} workerPools={{}} hasEditPermissions={rowCanBeFocused} sourceItems={undefined}/>);
        }
        const label = getLabel(props.data.Parameter);
        const options = selectOptionsToItems(props.data.Parameter.DisplaySettings["Octopus.SelectOptions"]);
        const packageParameterIsUsed = !!props.sourceItems.packages.items.find((p) => p.Properties["PackageParameterName"] === props.data.Parameter.Name);
        const value = props.action.Properties[props.data.Parameter.Name] || props.data.Value?.Value;
        const summary = createSummaryForParameterValue(label, controlType, options, props.sourceItems.packages.feeds, props.sourceItems.environments, props.sourceItems.workerPools.items, props.sourceItems.teams, props.sourceItems.channels, props.sourceItems.projects, packageParameterIsUsed)(value);
        return renderSummaryNodeForParameterValue(summary.node);
    };
    return (<div ref={containerRef}>
            {showMultipleValuesAndScopesIndicators(controlType, props.data.ValueIndex, props.data.Value, props.data.Parameter) && (<div className={styles.multipleValuesAndScopesIndicator}>
                    <div>Multiple values</div>
                    {isAccountControlType(controlType) && (<div className={styles.overrideButtonContainer}>
                            <PopoverBasicWithTrigger description={<div className={styles.popoverBasicWithTriggerContainer} style={{ width: containerRef.current ? `${containerRef.current.offsetWidth - 24}px` : space[24] }}>
                                        <ProcessTemplateParameter key={props.data.Parameter.Name} stepId={props.stepId} parameter={props.data.Parameter} sourceItems={props.sourceItems} action={props.action} onParameterValueChanged={props.onParameterValueChanged} doBusyTask={props.doBusyTask} analyticsStepEditorDispatch={props.analyticsStepEditorDispatch} autoFocus={rowIsFocused}/>
                                    </div>} placement="top-end" width="extra-wide" triggerElement={<IconButtonWithTooltip toolTipContent={tooltipContent} customIcon={<PlusIcon size={20} color={isOverrideVariableRow(props.data, props.sourceItems) ? themeTokens.color.icon.disabled : themeTokens.color.icon.secondary}/>} accessibleName={tooltipContent}/>} triggerAlignment="center" open={isOverrideVariableRow(props.data, props.sourceItems) ? false : undefined} contentPadding={0}/>
                        </div>)}
                </div>)}
            <div onFocus={onFocus} onClick={onFocus} className={styles.valueContainer}>
                <PopoverWhenFocused isFocused={rowIsFocused} onClickOutside={onBlur} isContentCentered={true} isFullWidth={true}>
                    {rowIsFocused ? (<div className={styles.popoverWhenFocusedContainer}>
                            <ProcessTemplateParameter key={props.data.Parameter.Name} stepId={props.stepId} parameter={props.data.Parameter} sourceItems={props.sourceItems} action={props.action} onParameterValueChanged={props.onParameterValueChanged} doBusyTask={props.doBusyTask} analyticsStepEditorDispatch={props.analyticsStepEditorDispatch} autoFocus={rowIsFocused}/>
                        </div>) : hasValue() ? (renderValue()) : (<div className={styles.noValue}>
                            <MissingVariablesIcon show={true}/>
                            <Text value={""} placeholder={"Provide value"} onChange={noOp} hideUnderline={true} monoSpacedFont={true}/>
                        </div>)}
                </PopoverWhenFocused>
            </div>
        </div>);
}
